import { BaseMode } from './BaseMode';
import { Modal } from '@/components/Modal';
import { Iframe } from '@/components/Iframe';
import { Spinner } from '@/components/Spinner';
import { UrlBuilder } from '@/core/UrlBuilder';
import type { WidgetConfig } from '@/types/config';
import type { EventBus } from '@/core/EventBus';
import type {
  UpdateIframeHeightMessage,
  UpdateIframeWidthMessage,
} from '@/types/messages';

/**
 * Overlay mode - displays widget in a modal overlay
 */
export class OverlayMode extends BaseMode {
  private modal: Modal | null = null;
  private iframe: Iframe | null = null;
  private spinner: Spinner | null = null;
  private modalId: string;
  private iframeId: string;
  private spinnerId: string;

  constructor(
    config: WidgetConfig,
    eventBus: EventBus,
    private button: HTMLButtonElement
  ) {
    super(config, eventBus);
    this.modalId = this.generateId('caredove_modal');
    this.iframeId = this.generateId('caredove_iframe');
    this.spinnerId = this.generateId('caredove_spinner');
  }

  public render(): void {
    // Setup button click handler
    this.button.addEventListener('click', this.handleButtonClick.bind(this));

    // Register PostMessage handlers
    this.registerMessageHandlers();
  }

  private handleButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.openModal();
  }

  private openModal(): void {
    // Generate URL
    const url = UrlBuilder.generateUrl(this.config);

    // Create iframe
    this.iframe = new Iframe(url, this.config, this.iframeId);
    const iframeElement = this.iframe.create();

    // Create container for spinner + iframe
    const container = document.createElement('div');
    container.id = this.spinnerId;
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = this.config.height || 'calc(100vh - 50px)';

    container.appendChild(iframeElement);

    // Create modal
    this.modal = new Modal({
      id: this.modalId,
      content: container,
      config: this.config,
      onShow: () => {
        this.onModalOpen();
      },
      onClose: () => {
        this.onModalClose();
      },
    });

    // Show modal
    this.modal.show();

    // Setup spinner
    this.spinner = new Spinner(this.spinnerId);
    this.spinner.show();
    this.spinner.autoHide(10000); // Auto-hide after 10s

    // Setup iframe load handler
    this.iframe.onLoad(() => {
      this.spinner?.hide();
      this.iframe?.focus();
      this.startLocationBroadcast();
    });

    // Set link if showLink is enabled
    if (this.config.showLink && this.modal) {
      this.modal.setLink(url);
    }
  }

  private onModalOpen(): void {
    console.log('[CaredoveWidget] Modal opened');
  }

  private onModalClose(): void {
    console.log('[CaredoveWidget] Modal closed');
    this.cleanup();
  }

  private registerMessageHandlers(): void {
    // Update iframe height
    this.eventBus.on('updateIframeHeight', (data) => {
      const message = data as UpdateIframeHeightMessage;
      if (this.config.variableHeight && this.iframe) {
        this.iframe.updateHeight(message.height);
      }
    });

    // Update iframe width
    this.eventBus.on('updateIframeWidth', (data) => {
      const message = data as UpdateIframeWidthMessage;
      if (this.config.variableWidth && this.iframe) {
        this.iframe.updateWidth(message.servicesWrapperWidth);
      }
    });

    // Close modal
    this.eventBus.on('close', (_data) => {
      this.modal?.close();
    });
  }

  private startLocationBroadcast(): void {
    if (!this.iframe) return;

    const iframeElement = this.iframe.getElement();
    if (!iframeElement) return;

    // Send location every second
    const intervalId = setInterval(() => {
      if (!iframeElement.contentWindow) {
        clearInterval(intervalId);
        return;
      }

      this.eventBus.sendToIframe(iframeElement, {
        message: 'embedLocation',
        embedLocation: {
          iFrameId: this.iframeId,
          embedLocation: window.location.href,
          cdMinHeight: this.config.minHeight,
        },
      });
    }, 1000);

    // Store interval for cleanup
    (this.iframe as any)._locationInterval = intervalId;
  }

  public cleanup(): void {
    // Clear location broadcast interval
    if (this.iframe && (this.iframe as any)._locationInterval) {
      clearInterval((this.iframe as any)._locationInterval);
    }

    this.spinner?.hide();
    this.iframe?.destroy();
    this.modal?.destroy();

    this.spinner = null;
    this.iframe = null;
    this.modal = null;
  }
}
